
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Table from "../../components/Table";
import AddDialog from "../../components/Modal/AddModal";
import SelectInput from "../../components/Select/SelectInput";
import SearchableSelect from "../../components/Select/SearchableSelect";
import { useAuth } from "../../context/AuthContext";
import { getCoachesService } from "../../services/coachService";
import { getMembers } from "../../services/membersService";
import { createAssignment, getAssignments } from "../../services/assignService";
import { showErrorToast } from "../../services/toastService";
import { hasPermission } from "../../utils/permissions";

type AssignmentRow = {
    _id: string;
    memberName: string;
    coachName: string;
    assignedAt: string;
};

type OptionItem = {
    value: string;
    label: string;
};

const toArray = (value: unknown): unknown[] => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
        const data = value as { data?: unknown; items?: unknown; coaches?: unknown; members?: unknown };
        if (Array.isArray(data.data)) return data.data;
        if (Array.isArray(data.items)) return data.items;
        if (Array.isArray(data.coaches)) return data.coaches;
        if (Array.isArray(data.members)) return data.members;
    }
    return [];
};

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const asString = (value: unknown): string => (typeof value === "string" ? value : "");

const buildFullName = (entry: Record<string, unknown>): string => {
    const firstName = asString(entry.firstName);
    const lastName = asString(entry.lastName);
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) return fullName;
    return asString(entry.name) || "-";
};

const normalizeDate = (value: unknown): string => {
    const raw = asString(value);
    if (!raw) return "-";
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    return date.toISOString().split("T")[0];
};

const readErrorMessage = (err: unknown, fallback: string): string => {
    if (!err || typeof err !== "object") return fallback;
    const errObj = err as { response?: { data?: { message?: string } } };
    return errObj.response?.data?.message ?? fallback;
};

const AssignPage = () => {
    const { user } = useAuth();
    const canEditAssign = hasPermission(user, "assign.write");

    const [rows, setRows] = useState<AssignmentRow[]>([]);
    const [coaches, setCoaches] = useState<OptionItem[]>([]);
    const [members, setMembers] = useState<OptionItem[]>([]);
    const [loadingMembers, setLoadingMembers] = useState(false);

    const [addOpen, setAddOpen] = useState(false);
    const [error, setError] = useState("");

    const fetchAssignments = useCallback(async () => {
        if (!user?.id) return;
        setError("");

        try {
            const response = await getAssignments();
            const normalized = response.map((entry) => {
                const assignment = asRecord(entry);
                const coach = asRecord(assignment.coach || assignment.coachId);
                const member = asRecord(assignment.member || assignment.memberId);

                return {
                    _id:
                        asString(assignment._id) ||
                        asString(assignment.id) ||
                        `${asString(assignment.memberId)}-${asString(assignment.coachId)}-${Math.random()}`,
                    memberName:
                        buildFullName(member) ||
                        asString(asRecord(assignment.memberId).firstName) ||
                        asString(assignment.memberName) ||
                        "-",
                    coachName:
                        buildFullName(coach) ||
                        asString(asRecord(assignment.coachId).firstName) ||
                        asString(assignment.coachName) ||
                        "-",
                    assignedAt: normalizeDate(assignment.assignedAt || assignment.createdAt),
                };
            });
            setRows(normalized);
        } catch (err: unknown) {
            console.error("Failed to fetch assignments", err);
            setError(readErrorMessage(err, "Failed to load assignments"));
        }
    }, [user?.id]);

    const fetchCoaches = useCallback(async () => {
        if (!user?.id) return;

        try {
            const coachesResponse = await getCoachesService();

            const coachOptions = toArray(coachesResponse).map((entry) => {
                const coach = asRecord(entry);
                return {
                    value: asString(coach._id) || asString(coach.id),
                    label: buildFullName(coach),
                };
            }).filter((option) => option.value);

            setCoaches(coachOptions);
        } catch (err) {
            console.error("Failed to fetch coaches", err);
            setError("Failed to load coaches");
        }
    }, [user?.id]);

    const searchMembers = useCallback(async (query: string) => {
        const trimmedQuery = query.trim();
        if (trimmedQuery.length < 3) {
            setMembers([]);
            return;
        }

        setLoadingMembers(true);
        try {
            const membersResponse = await getMembers({ page: 1, limit: 30, search: trimmedQuery });
            const memberOptions = toArray(membersResponse).map((entry) => {
                const member = asRecord(entry);
                return {
                    value: asString(member._id) || asString(member.id),
                    label: buildFullName(member),
                };
            }).filter((option) => option.value);

            setMembers(memberOptions);
        } catch (err) {
            console.error("Failed to search members", err);
            setMembers([]);
            setError("Failed to search members");
        } finally {
            setLoadingMembers(false);
        }
    }, []);

    useEffect(() => {
        fetchAssignments();
    }, [fetchAssignments]);

    const openAddDialog = async () => {
        setAddOpen(true);
        setMembers([]);
        await fetchCoaches();
    };

    const formik = useFormik({
        initialValues: {
            coachId: "",
            memberId: "",
        },
        validationSchema: Yup.object({
            coachId: Yup.string().required("Coach is required"),
            memberId: Yup.string().required("Member is required"),
        }),
        onSubmit: async (values, { resetForm }) => {
            if (!user?.id) {
                showErrorToast("User not logged in");
                return;
            }

            try {
                await createAssignment({
                    coachId: values.coachId,
                    memberId: values.memberId,
                });

                await fetchAssignments();
                resetForm();
                setAddOpen(false);
            } catch (err: unknown) {
                console.error("Failed to assign member to coach", err);
                showErrorToast(readErrorMessage(err, "Failed to assign member"));
            }
        },
    });

    const closeDialog = () => {
        setAddOpen(false);
        formik.resetForm();
    };

    const columns = useMemo(
        () => [
            { Header: "Member Name", accessor: "memberName" },
            { Header: "Coach Name", accessor: "coachName" },
            { Header: "Assigned At", accessor: "assignedAt" },
        ],
        []
    );

    return (
        <div className="p-6 w-full">
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Assign</h1>
                    <p className="text-sm text-gray-500">Assign members to coaches</p>
                </div>
            </div>

            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

            <Table
                columns={columns}
                data={rows}
                pageSize={10}
                add={canEditAssign}
                onOpen={openAddDialog}
                renew={false}
                checkbox={false}
            />

            <AddDialog title="Assign Member" isOpen={addOpen} onClose={closeDialog} maxWidth="max-w-xl">
                <form onSubmit={formik.handleSubmit} className="space-y-4">
                    <div>
                        <SelectInput
                            label="Coach"
                            name="coachId"
                            value={formik.values.coachId}
                            onChange={formik.handleChange}
                            options={coaches}
                        />
                        {formik.touched.coachId && formik.errors.coachId && (
                            <p className="text-red-500 text-sm mt-1">{formik.errors.coachId}</p>
                        )}
                    </div>

                    <div>
                        <SearchableSelect
                            label="Member"
                            name="memberId"
                            value={formik.values.memberId}
                            options={members}
                            placeholder="Search members..."
                            minSearchLength={3}
                            loading={loadingMembers}
                            onChange={(name, selectedValue) => formik.setFieldValue(name, selectedValue)}
                            onBlur={(name) => formik.setFieldTouched(name, true)}
                            onSearch={searchMembers}
                        />
                        {formik.touched.memberId && formik.errors.memberId && (
                            <p className="text-red-500 text-sm mt-1">{formik.errors.memberId}</p>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            className="px-4 py-2 rounded border border-gray-300"
                            onClick={closeDialog}
                        >
                            Cancel
                        </button>
                        <button type="submit" className="px-4 py-2 rounded bg-[#ac120c] text-white">
                            Assign
                        </button>
                    </div>
                </form>
            </AddDialog>
        </div>
    );
};

export default AssignPage;
